"use client";
import { useLanguage } from "@/hooks/use-language-context";
import { useState, useRef, useEffect } from "react";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";

type Props = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string | any;
  value?: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isPassword?: boolean;
  name?: string;
  multiple?: boolean;
  maxLength?: number;
  minLength?: number;
  lefIcon?: React.ReactNode;
  type?: string;
};

const CustomTextInput = ({
  label,
  placeholder,
  required,
  error,
  value = "",
  onChange,
  onBlur,
  disabled,
  className,
  style,
  name,
  isPassword = false,
  multiple,
  maxLength,
  type = "text",
  minLength,
  lefIcon,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useLanguage();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (multiple && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.max(70, textareaRef.current.scrollHeight)}px`;
    }
  }, [value, multiple]);

  const baseInputClasses = `
    h-[40px] w-full px-4 text-[14px] placeholder:text-xs placeholder:text-gray-300 text-gray-800 
     transition duration-200 ease-in-out focus:outline-none focus:ring-0
  `;

  const errorClasses = error
    ? "border-red-500 border focus:border-red-500"
    : "border-gray-200 focus:border-gray-400";

  const disabledClasses = disabled ? "bg-slate-100 cursor-not-allowed" : "";

  return (
    <div className="w-full flex flex-col">
      {label && (
        <label className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider block mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        className={`${className} relative ${errorClasses} rounded-sm overflow-hidden`}
      >
        {!multiple ? (
          <>
            {lefIcon && (
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-md">
                {lefIcon}
              </span>
            )}
            <input
              type={isPassword ? (!showPassword ? "password" : "text") : type}
              disabled={disabled}
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={
                isPassword ? "***************" : placeholder ?? t("Type")
              }
              className={`${baseInputClasses} ${disabledClasses} ${
                lefIcon ? "pl-10" : ""
              }`}
              maxLength={maxLength}
              minLength={minLength}
              style={style}
            />
          </>
        ) : (
          <textarea
            ref={textareaRef}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder ?? t("Type")}
            maxLength={maxLength ?? 4000}
            minLength={minLength ?? 0}
            disabled={disabled}
            //className={`${baseInputClasses} ${disabledClasses} py-2 resize-none`}
            //style={{ ...style, height: 70 }}
            className={`${baseInputClasses} ${disabledClasses} py-2 resize-none overflow-y-auto`}
            style={style}
          />
        )}
        {isPassword && (
          <span
            role="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center justify-center text-gray-400 hover:bg-gray-200 text-md cursor-pointer bg-gray-100 rounded-full h-7 w-7"
          >
            {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
          </span>
        )}
      </div>
      {error && <small className="text-red-500 text-xs mt-1">{error}</small>}
    </div>
  );
};

export default CustomTextInput;
